import React from "react";
/** Components */
import {
  ObserverDashboardWeb,
  ObserverMobileDashboard,
  ObserverOfflineChecklist,
} from "./Components";

interface BackgroundImage {
  value: any | null;
  title: string;
  description: string;
  key: string;
  appName: string;
  fileId: number | null;
  filePath: string | null;
}

type SettingsByApp = {
  isnAdmin?: BackgroundImage[];
  fishMap?: BackgroundImage[];
  skipperScience?: BackgroundImage[];
};

type ImageProps = {
  title: string;
  value: any | null;
  description: string;
  imageKey: string;
  fileId: number | null;
  filePath: string | null;
  appName: string;
  getUniversalSettings: () => void;
  setSettings: any;
};

type Props = {
  currentBrand: string;
  currentApp: "isn" | "fish_map" | "skipper_science";
  settings: SettingsByApp;
  getUniversalSettings: () => void;
  setSettings: any;
};

export const DefaultBackGroundImage: React.FC<Props> = ({
  currentBrand,
  currentApp,
  settings,
  getUniversalSettings,
  setSettings,
}) => {
  if (currentBrand !== "isn_admin") return null;

  // Keys to render, in order
  const ORDERED_KEYS = [
    "observer_mobile_dashboard_image",
    "observer_offline_checklist_image",
    "observer_dashboard_web_image",
  ] as const;
  type Key = (typeof ORDERED_KEYS)[number];

  // Component map with the correct prop type
  const COMPONENTS: Record<Key, React.FC<ImageProps>> = {
    observer_mobile_dashboard_image: ObserverMobileDashboard,
    observer_offline_checklist_image: ObserverOfflineChecklist,
    observer_dashboard_web_image: ObserverDashboardWeb,
  };

  // Select the correct settings array for the current app
  const settingsMap: Record<Props["currentApp"], BackgroundImage[]> = {
    isn: settings?.isnAdmin ?? [],
    fish_map: settings?.fishMap ?? [],
    skipper_science: settings?.skipperScience ?? [],
  };
  const appSettings = settingsMap[currentApp];

  // Build props per key from settings
  const propsByKey: Partial<Record<Key, ImageProps>> = {};
  for (const s of appSettings) {
    if ((ORDERED_KEYS as readonly string[]).includes(s.key)) {
      const k = s.key as Key;
      propsByKey[k] = {
        title: s.title,
        value: s.value,
        description: s.description,
        imageKey: s.key,
        fileId: s.fileId,
        filePath: s.filePath,
        appName: s.appName,
        getUniversalSettings,
        setSettings,
      };
    }
  }

  // Only render components that have data
  const keysToRender = ORDERED_KEYS.filter((k) => !!propsByKey[k]);
  if (keysToRender.length === 0) return null;

  return (
    <div className="grid grid-cols-4 w-full h-auto">
      {keysToRender.map((k) => {
        const C = COMPONENTS[k];
        return <C key={k} {...(propsByKey[k] as ImageProps)} />;
      })}
    </div>
  );
};
